import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import css from './survey.module.css';
import Header from '../../components/standartComponents/header';
import { getCSRFToken, fetchCSRFToken } from '../../lib/csrf';
import { useTranslation } from 'react-i18next';

const LEVEL_BADGE = { 1: 'A1', 2: 'A0', 3: 'A2', 4: 'B1', 5: 'B2', 6: 'C1', 7: 'pre-A1' };

const getLevelBadge = (levels) => {
    if (!levels?.length) return null;
    if (levels.length > 1) {
        return `${LEVEL_BADGE[levels[0]]}–${LEVEL_BADGE[levels[levels.length - 1]]}`;
    }
    return LEVEL_BADGE[levels[0]] || null;
};

// Используем ключи для переводов, чтобы избежать багов при смене языка (state не будет теряться)
const LANG_OPTIONS = [
    { code: 'uk', flag: 'UA', nameKey: 'Ukrainian' },
    { code: 'en', flag: 'EN', nameKey: 'English' },
    { code: 'pl', flag: 'PL', nameKey: 'Polish' },
];

const GOAL_OPTIONS = [
    { id: 'travel', titleKey: 'Travel', descKey: 'Airport, hotel, café, and meeting people abroad.', icon: '✈️', color: 'rgba(245,158,11,0.08)', purposeId: 1 },
    { id: 'work', titleKey: 'Work', descKey: 'Job interviews, meetings, correspondence, and workplace dialogues.', icon: '💼', color: 'rgba(99,102,241,0.08)', purposeId: 2 },
    { id: 'education', titleKey: 'Study', descKey: 'Educational topics, explanations, and academic vocabulary.', icon: '🎓', color: 'rgba(16,185,129,0.08)', purposeId: 3 },
    { id: 'communication', titleKey: 'Socializing', descKey: 'Small talk, meeting people, and everyday conversations.', icon: '💬', color: 'rgba(244,63,94,0.08)', purposeId: 4 },
    { id: 'fun', titleKey: 'For yourself', descKey: 'Everyday topics, hobbies, and light practice.', icon: '🎵', color: 'rgba(139,92,246,0.08)', purposeId: 5 },
];

const Survey = ({ authUser }) => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    
    const totalSteps = 3;
    const [currentStep, setCurrentStep] = useState(0);
    const [direction, setDirection] = useState('');
    const [animating, setAnimating] = useState(false);
    
    const [selLang, setSelLang] = useState(null); 
    const [selGoal, setSelGoal] = useState(null); 
    const [selLevel, setSelLevel] = useState(null); 
    
    const [levels, setLevels] = useState([]);
    const [levelsLoading, setLevelsLoading] = useState(false);
    const [levelsError, setLevelsError] = useState(null);
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentStep]);

    useEffect(() => {
        if (currentStep !== 2) return;
        const fetchLevels = async () => {
            setLevelsLoading(true);
            setLevelsError(null);
            try {
                const res = await fetch('/api/v1/language-level-questionnaire', {
                    credentials: 'include',
                    headers: { 'Accept-Language': selLang?.code || i18n.language || 'uk' },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                const results = data.results || [];
                const sorted = [...results];
                const preA1Idx = sorted.findIndex(l => (l.levels || []).includes(7));
                if (preA1Idx > 0) {
                    const [preA1] = sorted.splice(preA1Idx, 1);
                    sorted.unshift(preA1);
                }
                setLevels(sorted);
            } catch (err) {
                console.error('Error fetching levels:', err);
                // Сохраняем ключ перевода, а не сам текст
                setLevelsError('Failed to load levels. Please try again.');
            } finally {
                setLevelsLoading(false);
            }
        };
        fetchLevels();
    }, [currentStep, selLang?.code]);

    const patchProfile = async (payload) => {
        let csrfToken = getCSRFToken();
        if (!csrfToken) {
            await fetchCSRFToken();
            csrfToken = getCSRFToken();
        }
        const res = await fetch('/api/v1/user-profile/current', {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...(csrfToken && { 'X-CSRFToken': csrfToken }),
            },
            body: JSON.stringify(payload),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err?.detail || `HTTP ${res.status}`);
        }
        return res.json();
    };

    const goTo = (step) => {
        setDirection(step > currentStep ? 'forward' : 'backward');
        setAnimating(true);
        setTimeout(() => {
            setCurrentStep(step);
            setAnimating(false);
        }, 300);
    };

    const handleLangNext = async () => {
        if (!selLang) return;
        try {
            i18n.changeLanguage(selLang.code);
            await patchProfile({ language: selLang.code });
        } catch (err) {
            console.error('Failed to save language:', err);
        }
        goTo(1);
    };

    const handleGoalNext = () => {
        if (!selGoal) return;
        goTo(2);
    };

    const handleLevelNext = async () => {
        if (!selLevel || isSubmitting) return;
        setIsSubmitting(true);
        setSubmitError(null);
        try {
            const profileRes = await fetch('/api/v1/user-profile/current', { credentials: 'include' });
            const profile = profileRes.ok ? await profileRes.json() : {};
            const existingLevels = profile.english_levels || [];
            const existingPurposes = profile.english_purposes || [];
            const newLevelInts = selLevel.levels || [];
            const newPurposeId = selGoal.purposeId;
            const mergedLevels = [...existingLevels, ...newLevelInts.filter(lvl => !existingLevels.includes(lvl))];
            const mergedPurposes = existingPurposes.includes(newPurposeId) ? existingPurposes : [...existingPurposes, newPurposeId];
            await patchProfile({ english_levels: mergedLevels, english_purposes: mergedPurposes });
            goTo(3);
        } catch (err) {
            console.error('Failed to save survey results:', err);
            // Сохраняем ключ перевода
            setSubmitError('Failed to save results. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const pct = currentStep < totalSteps ? ((currentStep + 1) / totalSteps) * 100 : 100;
    const getStepClass = (stepIndex) => {
        if (animating) {
            if (stepIndex === currentStep) return direction === 'forward' ? css.exitLeft : css.exitRight;
            if (direction === 'forward' && stepIndex === currentStep + 1) return css.active;
            if (direction === 'backward' && stepIndex === currentStep - 1) return css.enterFromLeft;
        }
        return stepIndex === currentStep ? css.active : '';
    };

    return (
        <div className={css.surveyPage}>
            <div className={css.gridBg}></div>
            <Header authUser={authUser} />
            <main className={`${css.main} ${css.mainFadeIn}`}>
                {currentStep < totalSteps && (
                    <>
                        <div className={css.microTop}>
                            <div className={css.microBadge}>
                                <span className={css.microBadgeDot}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>
                                </span>
                                {t('Creating your personal plan')}
                            </div>
                            <div className={css.microSub}>{t('This will take less than 1 minute. Your answers will help us create the right plan.')}</div>
                        </div>
                        <div className={css.progressWrap}>
                            <div className={css.progressTop}>
                                <span className={css.progressStep}>{t('Step {{step}} of {{total}}', { step: currentStep + 1, total: totalSteps })}</span>
                                <div className={css.progressDots}>
                                    {[0, 1, 2].map(i => (
                                        <div key={i} className={`${css.progressDot} ${i < currentStep ? css.done : ''} ${i === currentStep ? css.active : ''}`}></div>
                                    ))}
                                </div>
                            </div>
                            <div className={css.progressBarWrap}>
                                <div className={css.progressBarFill} style={{ width: `${pct}%` }}></div>
                            </div>
                        </div>
                    </>
                )}
                
                {/* STEP 1: LANGUAGE */}
                <div className={`${css.stepView} ${getStepClass(0)}`}>
                    <div className={css.stepContainer}>
                        <div className={css.stepQuestion}>{t('Which language would you like to use for the interface?')}</div>
                        <div className={css.stepSubtitle}>{t('Choose the interface language for learning.')}</div>
                        <div className={`${css.optionsGrid} ${css.cols3}`}>
                            {LANG_OPTIONS.map(lang => (
                                <div key={lang.code} className={`${css.optCard} ${css.langCard} ${selLang?.code === lang.code ? css.selected : ''}`} onClick={() => setSelLang(lang)}>
                                    <div className={css.optIcon}>{lang.flag}</div>
                                    <div className={css.optBody}><div className={css.optTitle}>{t(lang.nameKey)}</div></div>
                                    <div className={css.optCheck}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg></div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={css.navBar}>
                        <button className={`${css.btnBack} ${css.hidden}`}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg> {t('Back')}
                        </button>
                        <button className={css.btnNext} disabled={!selLang} onClick={handleLangNext}>
                            {t('Next')} <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                        </button>
                    </div>
                </div>

                {/* STEP 2: GOAL */}
                <div className={`${css.stepView} ${getStepClass(1)}`}>
                    <div className={css.stepContainer}>
                        <div className={css.stepQuestion}>{t('Why are you learning English?')}</div>
                        <div className={css.stepSubtitle}>{t('We will select lesson topics based on your goal.')}</div>
                        <div className={`${css.optionsGrid} ${css.cols1}`}>
                            {GOAL_OPTIONS.map(opt => (
                                <div key={opt.id} className={`${css.optCard} ${selGoal?.id === opt.id ? css.selected : ''}`} onClick={() => setSelGoal(opt)}>
                                    <div className={css.optIcon} style={{ background: opt.color }}>{opt.icon}</div>
                                    <div className={css.optBody}>
                                        <div className={css.optTitle}>{t(opt.titleKey)}</div>
                                        <div className={css.optDesc}>{t(opt.descKey)}</div>
                                    </div>
                                    <div className={css.optCheck}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg></div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={css.navBar}>
                        <button className={css.btnBack} onClick={() => goTo(0)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg> {t('Back')}
                        </button>
                        <button className={css.btnNext} disabled={!selGoal} onClick={handleGoalNext}>
                            {t('Next')} <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                        </button>
                    </div>
                </div>

                {/* STEP 3: LEVEL */}
                <div className={`${css.stepView} ${getStepClass(2)}`}>
                    <div className={css.stepContainer}>
                        <div className={css.stepQuestion}>{t('What is your current English level?')}</div>
                        <div className={css.stepSubtitle}>{t('Choose the level to start learning.')}</div>
                        {levelsLoading ? (
                            <div className={css.lvLoading}>{t('Loading levels...')}</div>
                        ) : levelsError ? (
                            <div className={css.lvErr}>{t(levelsError)}</div>
                        ) : (
                            <div className={`${css.optionsGrid} ${css.cols1}`}>
                                {levels.map(lvl => (
                                    <div key={lvl.id} className={`${css.optCard} ${css.levelCard} ${selLevel?.id === lvl.id ? css.selected : ''}`} onClick={() => setSelLevel(lvl)}>
                                        {getLevelBadge(lvl.levels) && <span className={css.levelCode}>{getLevelBadge(lvl.levels)}</span>}
                                        <div className={css.optBody}>
                                            <div className={css.optTitle}>{lvl.option}</div>
                                        </div>
                                        <div className={css.optCheck}>
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className={css.helperNote}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                            {t('Not sure? Choose an approximate level — you can change it later.')}
                        </div>
                        {submitError && <div className={css.lvErr}>{t(submitError)}</div>}
                    </div>
                    <div className={css.navBar}>
                        <button className={css.btnBack} onClick={() => goTo(1)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg> {t('Back')}
                        </button>
                        <button className={css.btnNext} disabled={!selLevel || isSubmitting || levelsLoading} onClick={handleLevelNext}>
                            {isSubmitting ? t('Saving...') : t('Create plan')}
                            {!isSubmitting && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>}
                        </button>
                    </div>
                </div>

                {/* RESULT */}
                <div className={`${css.stepView} ${getStepClass(3)}`}>
                    <div className={css.resultContainer}>
                        <div className={css.resultIcon}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                        </div>
                        <h2 className={css.resultTitle}>{t('Your personal plan is ready')}</h2>
                        <p className={css.resultSub}>{t('We created a course based on your goal and level.')}</p>
                        <div className={css.planCard}>
                            <div className={css.planRow}>
                                <span className={css.planLabel}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="m2 12 10 4 10-4"/></svg> {t('Interface language')}
                                </span>
                                <span className={css.planValue}>{selLang ? t(selLang.nameKey) : '—'}</span>
                            </div>
                            <div className={css.planRow}>
                                <span className={css.planLabel}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" x2="9.01" y1="9" y2="9"/><line x1="15" x2="15.01" y1="9" y2="9"/></svg> {t('Goal')}
                                </span>
                                <span className={css.planValue}>{selGoal ? t(selGoal.titleKey) : '—'}</span>
                            </div>
                            <div className={css.planRow}>
                                <span className={css.planLabel}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg> {t('Level')}
                                </span>
                                <span className={css.planValue}>{selLevel?.option || '—'}</span>
                            </div>
                        </div>
                        <div className={css.resultActions}>
                            <button className={css.btnStart} onClick={() => navigate('/roadMap')}>
                                {t('Start first lesson')} <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                            </button>
                            <button className={css.btnEdit} onClick={() => goTo(0)}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                                {t('Change answers')}
                            </button>
                        </div>
                        <p className={css.resultNote}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                            {t('You can change the level or learning topic later in the plan settings.')}
                        </p>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Survey;